import { useQueryClient } from "@tanstack/react-query";

import type {
  ChatMessageDto,
  ConversationSummaryDto,
  ConversationThreadDto,
  DeliveryStatus,
  ImportDeliveriesInput,
  ImportDeliveriesResultDto,
  InboxCountsDto,
  MarkReadInput,
  PagedQuery,
  ReactToMessageInput,
  PagedResult,
  SendMessageInput,
  SendTemplateInput,
  SetStarredInput,
  StartConversationInput,
  UpdateDeliveryInput,
} from "@mandoob/shared";

import {
  type MutationHookOptions,
  type QueryHookOptions,
  type RequestOptions,
  useApiMutation,
  useApiQuery,
} from "@/api/query-helpers";
import { api } from "@/lib/api-client";

const BASE = "/api/inbox";

export interface ConversationsQueryParams extends PagedQuery {
  Filter?: string;
  Starred?: boolean;
  /** "Undelivered" is a pseudo-status covering New + Ongoing. */
  DeliveryStatus?: DeliveryStatus | "Undelivered";
  /**
   * Pass true for the archive view (chats closed for 24h+); leave undefined
   * for the normal views (the API coerces any present value to true).
   */
  Archived?: true;
}

export interface ThreadQueryParams {
  SkipCount?: number;
  MaxResultCount?: number;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const getConversations = (
  params?: ConversationsQueryParams,
  options?: RequestOptions,
  signal?: AbortSignal,
) =>
  api<PagedResult<ConversationSummaryDto>>(
    { url: `${BASE}/conversations`, method: "GET", params, signal },
    options,
  );

export const getGetConversationsQueryKey = (
  params?: ConversationsQueryParams,
) => [`${BASE}/conversations`, ...(params ? [params] : [])] as const;

export const useGetConversations = (
  params?: ConversationsQueryParams,
  options?: QueryHookOptions<PagedResult<ConversationSummaryDto>>,
) =>
  useApiQuery(
    getGetConversationsQueryKey(params),
    (request, signal) => getConversations(params, request, signal),
    options,
  );

export const getThread = (
  conversationId: string,
  params?: ThreadQueryParams,
  options?: RequestOptions,
  signal?: AbortSignal,
) =>
  api<ConversationThreadDto>(
    {
      url: `${BASE}/conversations/${conversationId}/thread`,
      method: "GET",
      params,
      signal,
    },
    options,
  );

export const getGetThreadQueryKey = (
  conversationId: string,
  params?: ThreadQueryParams,
) =>
  [
    `${BASE}/conversations/${conversationId}/thread`,
    ...(params ? [params] : []),
  ] as const;

export const useGetThread = (
  conversationId: string,
  params?: ThreadQueryParams,
  options?: QueryHookOptions<ConversationThreadDto>,
) =>
  useApiQuery(
    getGetThreadQueryKey(conversationId, params),
    (request, signal) => getThread(conversationId, params, request, signal),
    {
      ...options,
      query: { enabled: !!conversationId, ...options?.query },
    },
  );

export const getCounts = (options?: RequestOptions, signal?: AbortSignal) =>
  api<InboxCountsDto>(
    { url: `${BASE}/counts`, method: "GET", signal },
    options,
  );

export const getGetCountsQueryKey = () => [`${BASE}/counts`] as const;

export const useGetCounts = (options?: QueryHookOptions<InboxCountsDto>) =>
  useApiQuery(
    getGetCountsQueryKey(),
    (request, signal) => getCounts(request, signal),
    options,
  );

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const sendMessage = (data: SendMessageInput, options?: RequestOptions) =>
  api<ChatMessageDto>(
    { url: `${BASE}/send-message`, method: "POST", data },
    options,
  );

export const useSendMessage = (
  options?: MutationHookOptions<ChatMessageDto, SendMessageInput>,
) => useApiMutation((data, request) => sendMessage(data, request), options);

export const sendTemplate = (
  data: SendTemplateInput,
  options?: RequestOptions,
) =>
  api<ChatMessageDto>(
    { url: `${BASE}/send-template`, method: "POST", data },
    options,
  );

export const useSendTemplate = (
  options?: MutationHookOptions<ChatMessageDto, SendTemplateInput>,
) => useApiMutation((data, request) => sendTemplate(data, request), options);

export const reactToMessage = (
  data: ReactToMessageInput,
  options?: RequestOptions,
) =>
  api<ChatMessageDto>({ url: `${BASE}/react`, method: "POST", data }, options);

/** Set/replace/remove ("" emoji) the driver's reaction; refreshes the thread. */
export const useReactToMessage = (
  options?: MutationHookOptions<ChatMessageDto, ReactToMessageInput>,
) => {
  const queryClient = useQueryClient();
  return useApiMutation((data, request) => reactToMessage(data, request), {
    ...options,
    mutation: {
      ...options?.mutation,
      onSuccess: (...args) => {
        const [message] = args;
        void queryClient.invalidateQueries({
          queryKey: [`${BASE}/conversations/${message.conversationId}/thread`],
        });
        options?.mutation?.onSuccess?.(...args);
      },
    },
  });
};

export const startConversation = (
  data: StartConversationInput,
  options?: RequestOptions,
) =>
  api<ConversationSummaryDto>(
    { url: `${BASE}/start-conversation`, method: "POST", data },
    options,
  );

export const useStartConversation = (
  options?: MutationHookOptions<ConversationSummaryDto, StartConversationInput>,
) =>
  useApiMutation((data, request) => startConversation(data, request), options);

export const importDeliveries = (
  data: ImportDeliveriesInput,
  options?: RequestOptions,
) =>
  api<ImportDeliveriesResultDto>(
    { url: `${BASE}/import-deliveries`, method: "POST", data },
    options,
  );

/**
 * Bulk import of a CSV/Excel batch. Each row starts a delivery and sends the
 * chosen template, so the conversation list is refreshed once the batch lands.
 */
export const useImportDeliveries = (
  options?: MutationHookOptions<
    ImportDeliveriesResultDto,
    ImportDeliveriesInput
  >,
) => {
  const queryClient = useQueryClient();
  return useApiMutation((data, request) => importDeliveries(data, request), {
    ...options,
    mutation: {
      ...options?.mutation,
      onSuccess: (...args) => {
        void queryClient.invalidateQueries({
          queryKey: [`${BASE}/conversations`],
        });
        void queryClient.invalidateQueries({ queryKey: [`${BASE}/counts`] });
        options?.mutation?.onSuccess?.(...args);
      },
    },
  });
};

export const updateDelivery = (
  data: UpdateDeliveryInput,
  options?: RequestOptions,
) =>
  api<ConversationSummaryDto>(
    { url: `${BASE}/update-delivery`, method: "POST", data },
    options,
  );

/**
 * Edits a conversation's delivery fields/status and refreshes the list and the
 * thread on success (callers can still chain their own onSuccess).
 */
export const useUpdateDelivery = (
  options?: MutationHookOptions<ConversationSummaryDto, UpdateDeliveryInput>,
) => {
  const queryClient = useQueryClient();
  return useApiMutation((data, request) => updateDelivery(data, request), {
    ...options,
    mutation: {
      ...options?.mutation,
      onSuccess: (...args) => {
        const [, variables] = args;
        void queryClient.invalidateQueries({
          queryKey: [`${BASE}/conversations`],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            `${BASE}/conversations/${variables.conversationId}/thread`,
          ],
        });
        options?.mutation?.onSuccess?.(...args);
      },
    },
  });
};

export const markRead = (data: MarkReadInput, options?: RequestOptions) =>
  api<void>({ url: `${BASE}/mark-read`, method: "POST", data }, options);

export const useMarkRead = (
  options?: MutationHookOptions<void, MarkReadInput>,
) => useApiMutation((data, request) => markRead(data, request), options);

// Re-closes a reopened (finished) conversation — frees the client number.
export const closeConversation = (
  data: { conversationId: string },
  options?: RequestOptions,
) =>
  api<ConversationSummaryDto>(
    { url: `${BASE}/close-conversation`, method: "POST", data },
    options,
  );

export const useCloseConversation = (
  options?: MutationHookOptions<
    ConversationSummaryDto,
    { conversationId: string }
  >,
) =>
  useApiMutation((data, request) => closeConversation(data, request), options);

export const setStarred = (data: SetStarredInput, options?: RequestOptions) =>
  api<void>({ url: `${BASE}/set-starred`, method: "POST", data }, options);

export const useSetStarred = (
  options?: MutationHookOptions<void, SetStarredInput>,
) => useApiMutation((data, request) => setStarred(data, request), options);
